import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook to manage subscription status and premium feature access
 */
export function useSubscription(user) {
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState(null);

  // Calculate subscription status
  const subscriptionStatus = useMemo(() => {
    if (!user) return { isActive: true, isExpired: false, daysRemaining: 30, status: 'trial' };
    
    const status = user.subscription_status || 'trial';
    const endDate = user.subscription_end ? new Date(user.subscription_end) : null;
    const now = new Date();
    
    if (status === 'active') {
      return { isActive: true, isExpired: false, daysRemaining: null, status: 'active' };
    }
    
    if (status === 'trial' && endDate) {
      const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      const isExpired = daysRemaining <= 0;
      return { 
        isActive: !isExpired, 
        isExpired, 
        daysRemaining: Math.max(0, daysRemaining),
        status: 'trial'
      };
    }
    
    if (status === 'expired' || status === 'cancelled') {
      return { isActive: false, isExpired: true, daysRemaining: 0, status };
    }
    
    return { isActive: true, isExpired: false, daysRemaining: 30, status };
  }, [user]);

  // Check if a premium feature can be accessed
  const canAccessPremiumFeature = useCallback(() => {
    return subscriptionStatus.isActive;
  }, [subscriptionStatus.isActive]);

  // Check if user can log/add data
  const canLogData = useCallback(() => {
    return subscriptionStatus.isActive;
  }, [subscriptionStatus.isActive]);

  // Handle premium feature access with paywall
  const handlePremiumFeatureAccess = useCallback((featureName, callback) => {
    if (canAccessPremiumFeature()) {
      callback();
    } else {
      setPaywallTrigger(featureName);
      setShowPaywall(true);
    }
  }, [canAccessPremiumFeature]);

  // Handle any feature that requires logging data
  const handleFeatureAccess = useCallback((featureName, callback) => {
    if (canLogData()) {
      callback();
    } else {
      setPaywallTrigger(featureName);
      setShowPaywall(true);
    }
  }, [canLogData]);

  // Close paywall
  const closePaywall = useCallback(() => {
    setShowPaywall(false);
    setPaywallTrigger(null);
  }, []);

  return {
    subscriptionStatus,
    showPaywall,
    paywallTrigger,
    canAccessPremiumFeature,
    canLogData,
    handlePremiumFeatureAccess,
    handleFeatureAccess,
    closePaywall,
    setShowPaywall,
  };
}

export default useSubscription;
